import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';

import { useAvaUsers, useCurrentAvaPlan } from '@/api';
import { useOffcanvasPageContext } from '@/contexts';
import { useDocumentTitle } from '@/hooks';
import { settingsManageUpdateAvaPlanPath } from '@/routes';
import { pluralize } from '@/utils';

import ActivateAvaContent from '@/components/Billing/ActivateAvaContent';
import AvaUsersList from '@/components/Billing/AvaUsersList';
import Button from '@/components/Button';
import Card from '@/components/Card';
import Footer from '@/components/Footer';
import Icon from '@/components/Icon';
import Main from '@/components/Main';
import SkeletonWrapper from '@/components/SkeletonWrapper';

import error from 'images/error-js.svg';

function Invite({ currentPlanData, avaListUsers, setShowInviteUsersButtonFooter }) {
  const totalConnectedAvaUsers = avaListUsers.length;
  const maxUsersPlan = currentPlanData.maxUsersCount;
  const availableInvites = maxUsersPlan - totalConnectedAvaUsers;
  const hasAvailableInvites = Boolean(availableInvites);
  const hasUsersConnected = Boolean(totalConnectedAvaUsers);
  const [
    variantContent,
    setVariantContent
  ] = useState(hasUsersConnected ? 'users-list' : 'activate-ava');
  const showInviteSection = hasAvailableInvites && variantContent === 'users-list';

  useEffect(() => {
    setShowInviteUsersButtonFooter(!hasAvailableInvites && variantContent === 'users-list');
  }, [hasAvailableInvites]);

  const activeUsersMessage = () => {
    if (hasUsersConnected) {
      const usersText = pluralize('usuário', maxUsersPlan);
      const activeText = pluralize('ativo', maxUsersPlan);

      return `${totalConnectedAvaUsers} de ${maxUsersPlan} ${usersText} ${activeText}`;
    }

    const usersText = pluralize('usuário', totalConnectedAvaUsers);
    const activeText = pluralize('ativo', totalConnectedAvaUsers);

    return `${totalConnectedAvaUsers} ${usersText} ${activeText}`;
  };

  const availableInvitesMessage = () => {
    const availableText = availableInvites === 1 ? 'disponível' : 'disponíveis';
    const invitesText = pluralize('convite', availableInvites);

    return `${availableInvites} ${invitesText} ${availableText}`;
  };

  const renderAvaUsersListContent = () => (
    <Card className='p-2 border-light' style={{ minWidth: '502px' }}>
      <AvaUsersList usersList={avaListUsers} />
    </Card>
  );

  const renderActivateAvaContent = () => (
    <Card
      className='d-flex py-3 px-2 flex-column text-center align-items-center border-light'
    >
      <ActivateAvaContent />
    </Card>
  );

  const CONTENTS = {
    'users-list': renderAvaUsersListContent,
    'activate-ava': renderActivateAvaContent
  };

  const renderContent = CONTENTS[variantContent];

  return (
    <>
      { showInviteSection && (
        <Card className={
          'd-flex py-3 px-2 mb-3 border-light flex-column text-center align-items-center'
        }>
          <div className='fw-bold d-flex flex-row justify-content-between w-100'>
            <div>
              <div className='d-inline-block py-1 ps-2 pe-1 me-2'>
                <div className={
                  'p-2 lh-0 bg-primary rounded position-relative bg-opacity-10 text-dark-gray'
                }>
                  <Icon size='sm' name='email-sent' />
                </div>
              </div>
              <span>{availableInvitesMessage()}</span>
            </div>
            <Button onClick={() => setVariantContent('activate-ava')}>
              <Icon className='me-2' name='add'/>
              Convidar
            </Button>
          </div>
        </Card>
      ) }
      { renderContent() }
      <div className='text-dark-gray text-small mt-2'>
        <span className='me-3'>
          <Icon name='team' className='me-1' /> {activeUsersMessage()}
        </span>
        { hasAvailableInvites && (
          <span>
            <Icon name='email-sent' className='me-1' /> {availableInvitesMessage()}
          </span>
        ) }
      </div>
    </>
  );
}

function InviteWrapper() {
  const PAGE_TITLE = 'Usuários da Ava';
  useDocumentTitle(PAGE_TITLE);

  const canvasContext = useOffcanvasPageContext();
  const handleClose = canvasContext.hide;

  const navigate = useNavigate();

  const [showContentError, setShowContentError] = useState(false);
  const [showInviteUsersButtonFooter, setShowInviteUsersButtonFooter] = useState(false);

  const {
    data: { data: currentPlanData } = {},
    isLoading: isLoadingCurrentPlan
  } = useCurrentAvaPlan();

  const {
    data: { data: avaListUsers } = {},
    isLoading: isLoadingAvaListUsers
  } = useAvaUsers({
    config: { onError: () => setShowContentError(true) },
    params: { active: true }
  });

  return (
    <>
      <Main fluid className='pt-6 px-5'>
        <Container className='d-flex h-100 flex-column'>
          <div className='d-flex align-items-start'>
            <div className='flex-fill'>
              <h2 className='mb-2'>{PAGE_TITLE}</h2>
            </div>

            <Button
              className='rounded border-0 px-2 py-1'
              variant='light-gray'
              type='button'
              aria-label='Fechar'
              onClick={handleClose}
            >
              <Icon className='text-dark-gray' name='close' />
            </Button>
          </div>

          <hr className='mb-7' />
          <SkeletonWrapper
            isLoading={isLoadingAvaListUsers || isLoadingCurrentPlan}
            SkeletonComponent={AvaUsersList.Skeleton}
          >
            {showContentError
              ? <ErrorList />
              : <Invite
                currentPlanData={currentPlanData}
                avaListUsers={avaListUsers}
                setShowInviteUsersButtonFooter={setShowInviteUsersButtonFooter}
              />
            }
          </SkeletonWrapper>
        </Container>
      </Main>
      <Footer className='mt-auto flex-shrink-0 pt-1 pb-6 ps-6 me-8' >
        <Container>
          <Row>
            <Col md={{ offset: 1, span: 10 }} className='d-flex'>
              <div className='ms-auto'>
                <Button
                  className='me-3 text-darker-gray'
                  variant='light-gray'
                  onClick={handleClose}
                >
                  Fechar
                </Button>
                {showInviteUsersButtonFooter && (
                  <Button
                    className='me-3'
                    onClick={() => navigate(settingsManageUpdateAvaPlanPath())}
                  >
                    Convidar usuário
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </Container>
      </Footer>
    </>
  );
}

function ErrorList() {
  const reload = () => {
    window.location.reload();
  };

  return (
    <Card className='p-9 border-light'>
      <div className={
        'd-flex flex-column align-items-center justify-content-center text-center py-9 px-4 my-9'
      }>
        <img src={error} />
        <span className='fw-bold text-dark-gray'>
          Parece que algo deu errado no carregamento das informações.
        </span>
        <Button
          size='sm'
          className='text-nowrap mt-7'
          onClick={() => reload()}
        >
          <Icon name='loading' className='me-1'/>
          Recarregar página
        </Button>
      </div>
    </Card>
  );
}

export default InviteWrapper;
